import React, { useState, useEffect, useRef, useMemo } from "react";
import { Redirect, Route, Link, useLocation } from "react-router-dom";
import auth from "services/auth";
import { gameTitles, gameTypeIcons, getGamesPageCategoryPath } from "configs/gameTypes";
import api from "services/api";
// import emailValidate from "services/emailValidate";
import GameItem from "elements/gameElements/GameItem";
import Translator, { __ } from "services/Translator";
import { Button } from "react-bootstrap";
import Slider from "react-slick";
import GameMenu from "elements/gameElements/GameMenu";
import useCountryRefetch from "hooks/useCountryRefetch";
import useShowUnavailable from "hooks/useShowUnavailable";
import { buildSliderGames } from "services/gameAvailability";
//New structured import
import { AngleRight, AngleLeft } from "components/Icons";
export default function GameSlider(props) {
  // const menuOpen = props.menuOpen;
  const location = useLocation();
  const [categoryName, setCategoryName] = useState(props.categoryName);
  const [isAuthenticated, setIsAuthenticated] = useState(auth.isAuthenticated);
  const [gameListsData, setGameListsData] = useState([]);
  const [popularSlots, setPopularSlots] = useState([]);
  const [tableGames, setTableGames] = useState([]);
  const category = props.category;
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(props.perPage);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPrevDisabled, setIsPrevDisabled] = useState(true);
  const [isNextDisabled, setIsNextDisabled] = useState(false);
  const specificGamesKey = JSON.stringify(props.specificGames ?? []);
  const prevCategoryRef = useRef(category);
  const prevSpecificGamesKeyRef = useRef(specificGamesKey);
  // Bumps on a country switch so the slider refetches for the new scope/currency.
  const countryNonce = useCountryRefetch();
  const countryFirstRef = useRef(true);
  // Home rows carry no sort header, so there is no toggle UI here — the slider
  // only honours whatever the player last chose on a category page.
  const [showUnavailable] = useShowUnavailable();

  const getGames = async () => {
    var response = await api.post("/games/by-category", {
      per_page: perPage,
      page: page,
      game_types: [category],
      specific_games: props.specificGames,
    });

    if (response.status === 200 && response.data) {
      const newGames = response.data.games[category]?.gamelist ?? [];
      setGameListsData((prev) =>
        page === 1 ? newGames : prev.concat(newGames)
      );

      // if (category == "video_slots") {
      //   const tempGameList = gameListsData;
      //   var mergedGameList = tempGameList.concat(response.data.games.video_slots.gamelist);
      //   setGameListsData(mergedGameList);
      // } else if (category == "table_games") {
      //   const tempGameList = gameListsData;
      //   var mergedGameList = tempGameList.concat(response.data.games.table_games.gamelist);
      //   setGameListsData(mergedGameList);
      // } else if (category == "video_poker") {
      //   const tempGameList = gameListsData;
      //   var mergedGameList = tempGameList.concat(response.data.games.video_poker.gamelist);
      //   setGameListsData(mergedGameList);
      // } else if (category == "live_casino") {
      //   const tempGameList = gameListsData;
      //   var mergedGameList = tempGameList.concat(response.data.games.live_casino.gamelist);
      //   setGameListsData(mergedGameList);
      // } else if (category == "new") {
      //   const tempGameList = gameListsData;
      //   var mergedGameList = tempGameList.concat(response.data.games.new.gamelist);

      //   setGameListsData(mergedGameList);
      // } else if (category == "popular") {
      //   const tempGameList = gameListsData;
      //   var mergedGameList = tempGameList.concat(response.data.games.popular.gamelist);
      //   setGameListsData(mergedGameList);
      // } else if (category == "jackpot") {
      //   const tempGameList = gameListsData;
      //   var mergedGameList = tempGameList.concat(response.data.games.jackpot.gamelist);
      //   setGameListsData(mergedGameList);
      // }
    }
  };

  // Reset list and page only when category or specificGames content changes
  useEffect(() => {
    if (!category) return;

    const categoryChanged = prevCategoryRef.current !== category;
    const specificGamesChanged =
      prevSpecificGamesKeyRef.current !== specificGamesKey;

    if (categoryChanged || specificGamesChanged) {
      setGameListsData([]);
      setPage(1);
      prevCategoryRef.current = category;
      prevSpecificGamesKeyRef.current = specificGamesKey;
    }
  }, [category, specificGamesKey]);

  // On a country switch, reset to page 1 so the refetch shows the new scope's
  // ordering from the start of the slider.
  useEffect(() => {
    if (countryFirstRef.current) {
      countryFirstRef.current = false;
      return;
    }
    setGameListsData([]);
    setPage(1);
  }, [countryNonce]);

  useEffect(() => {
    if (category) {
      getGames();
    }
  }, [category, page, perPage, specificGamesKey, countryNonce]);

  // Function to get current slidesToShow based on screen width
  const getCurrentSlidesToShow = () => {
    const width = window.innerWidth;
    if (width >= 1400) return 8; // Large screens
    if (width >= 992) return 8;   // Medium screens
    if (width >= 768) return 6;   // Small screens
    return 2.5; // Mobile
  };

  // Filter FIRST, then pad from the filtered array: padding from an emptied list
  // would push undefined into GameItem and crash on game.id. showUnavailable is
  // a real input, so it must stay in the dep array or the row freezes stale.
  const displayedGames = useMemo(
    () =>
      buildSliderGames(
        gameListsData,
        Math.ceil(getCurrentSlidesToShow()),
        showUnavailable
      ),
    [gameListsData, showUnavailable]
  );

  useEffect(() => {
    // Initialize disabled states when game data changes
    if (displayedGames.length > 0) {
      setIsPrevDisabled(true); // Start at the beginning
      const slidesToShow = getCurrentSlidesToShow();
      setIsNextDisabled(displayedGames.length <= slidesToShow); // Disable next if not enough items
    }
  }, [displayedGames]);

  // Add window resize listener to update disabled states on screen size change
  useEffect(() => {
    const handleResize = () => {
      if (displayedGames.length > 0) {
        const slidesToShow = getCurrentSlidesToShow();
        const maxSlide = Math.max(0, displayedGames.length - slidesToShow);
        setIsNextDisabled(currentSlide >= maxSlide);
        setIsPrevDisabled(currentSlide === 0);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [displayedGames.length, currentSlide]);

  const loadMoreGamesAction = (prm) => {
    setPage((prev) => prev + 1);
    setPerPage(24);
  };

  // games settings
  const gameNextLoad = () => {
    if (!isNextDisabled) {
      sliderRef.current.slickNext();
    }
  };

  const gamePrevLoad = () => {
    if (!isPrevDisabled) {
      sliderRef.current.slickPrev();
    }
  };

  const handleBeforeChange = (oldIndex, newIndex) => {
    setCurrentSlide(newIndex);
  };

  const handleAfterChange = (current) => {
    setCurrentSlide(current);

    // Check if we're at the beginning
    setIsPrevDisabled(current === 0);

    // Check if we're at the end
    const totalSlides = displayedGames.length;
    const slidesToShow = getCurrentSlidesToShow();

    // Calculate the maximum slide position
    const maxSlide = Math.max(0, totalSlides - slidesToShow);
    setIsNextDisabled(current >= maxSlide);
  };

  // Ensure we don't try to show more slides than we have games
  const getAdjustedSlidesToShow = (defaultSlides) => {
    return Math.min(defaultSlides, displayedGames.length || 1);
  };

  const gamesSettings = {
    dots: false,
    infinite: false,
    speed: 500,
    slidesToShow: getAdjustedSlidesToShow(8),
    slidesToScroll: getAdjustedSlidesToShow(8),
    autoplay: false,
    autoplaySpeed: 5000,
    initialSlide: 0,
    arrows: false,
    responsive: [
      {
        breakpoint: 1400,
        settings: {
          slidesToShow: getAdjustedSlidesToShow(8),
          slidesToScroll: getAdjustedSlidesToShow(8),
          infinite: false,
          dots: true,
        },
      },
      {
        breakpoint: 991,
        settings: {
          slidesToShow: getAdjustedSlidesToShow(6),
          slidesToScroll: getAdjustedSlidesToShow(6),
          infinite: false,
          dots: true,
        },
      },
      {
        breakpoint: 767,
        settings: {
          slidesToShow: getAdjustedSlidesToShow(2.5),
          slidesToScroll: 1,
          infinite: false,
          dots: true,
          swipeToSlide: true,
        },
      },
    ],
  };

  const sliderRef = useRef();
  const gamesCategoryPath = getGamesPageCategoryPath(props.category);
  const sliderHeadingText =
    props.categoryLabel ??
    props.categoryName ??
    gameTitles[props.category] ??
    "";
  const sliderCategoryIcon = gameTypeIcons[props.category];

  // A row the availability filter emptied must vanish whole — heading, GameMenu
  // and prev/next arrows included — instead of leaving a titled empty shell.
  // An empty RAW list is the pre-fetch state and keeps its loading placeholder.
  const hasRawGames = Array.isArray(gameListsData) && gameListsData.length > 0;
  if (hasRawGames && displayedGames.length === 0) return null;

  return (
    <section className="gameSlider">
      <div className=" game_container padding_top_bottom_30">
        <div className="gameSliderHeading">
          <div className="gameAction">
            {props.title == true && (
              <div className="gameCategoryHeading__titleGroup">
                {sliderCategoryIcon ? (
                  <span
                    className="gameCategoryHeading__iconWrap"
                    aria-hidden="true"
                  >
                    {sliderCategoryIcon}
                  </span>
                ) : null}
                <h2 className="gameCategoryHeading__titleText">
                  {sliderHeadingText}
                </h2>
              </div>
            )}

            {props.menuBar != false && (
              <GameMenu callFromSlider={true} {...props} />
            )}



            <div className="slideAction">
              <Link
                hreflang={Translator.getSelectedLanguage().code}
                to={"/games/" + gamesCategoryPath}
                className=" commonBtn pTwoBtn"
              >
                {__("View All")}
              </Link>
              <Button
                onClick={gamePrevLoad}
                className={`action_btn ${isPrevDisabled ? "disabled" : ""}`}
                disabled={isPrevDisabled}
              >
                <AngleLeft
                  width="12"
                  height="12"
                  fillColor={isPrevDisabled ? "#ccc" : "#4b486e"}
                />
              </Button>
              <Button
                onClick={gameNextLoad}
                className={`action_btn ${isNextDisabled ? "disabled" : ""}`}
                disabled={isNextDisabled}
              >
                <AngleRight
                  width="12"
                  height="12"
                  fillColor={isNextDisabled ? "#ccc" : "#4b486e"}
                />
              </Button>
            </div>
          </div>
        </div>

        {displayedGames.length > 0 ? (
          <Slider
            ref={sliderRef}
            {...gamesSettings}
            beforeChange={handleBeforeChange}
            afterChange={handleAfterChange}
          >
            {displayedGames.map((game, index) => (
              <GameItem
                key={`${game?.id ?? "game"}-${index}`}
                game={game}
                isAuthenticated={props.isAuthenticated}
              />
            ))}
          </Slider>
        ) : (
          <div style={{ padding: '20px', textAlign: 'center', color: 'white' }}>
            <p>Loading games...</p>
          </div>
        )}
      </div>
    </section>
  );
}
